import { ChangeDetectionStrategy, Component, effect, signal, Type, WritableSignal } from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { Router, RouterOutlet } from '@angular/router';
import { PwaInstallPromptComponent } from '@/shared/components/pwa-install-prompt';
import { FeedbackService } from '@/features/feedback/feedback.service';

import { AppRoutingPathsEnum } from '@/shared/config/app-routing-paths.enum';
import { MainRoutingPathsEnum } from '@/shared/config/main-routing-paths.enum';
import { isSubscriptionActive } from '@/shared/models/tariff.model';
import { AuthService } from '@/shared/services/auth.service';
import { NavigationTrackingService } from '@/shared/services/navigation-tracking.service';
import { PaymentRequiredService } from '@/shared/services/payment-required.service';

@Component({
  selector: 'idoc-start',
  templateUrl: './app.component.html',
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RouterOutlet, NgComponentOutlet, PwaInstallPromptComponent],
})
export class AppComponent {
  protected readonly feedbackModal$: WritableSignal<Type<unknown> | null>;
  protected readonly paymentRequiredModal$: WritableSignal<Type<unknown> | null>;

  private feedbackModalLoading: boolean;
  private paymentRequiredModalLoading: boolean;

  constructor(
    private readonly feedbackService: FeedbackService,
    private readonly paymentRequiredService: PaymentRequiredService,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly navigationTracking: NavigationTrackingService,
  ) {
    this.feedbackModal$ = signal(null);
    this.feedbackModalLoading = false;
    this.paymentRequiredModal$ = signal(null);
    this.paymentRequiredModalLoading = false;

    effect(() => {
      if (this.feedbackService.visible$() && !this.feedbackModal$() && !this.feedbackModalLoading) {
        this.feedbackModalLoading = true;
        import('@/features/feedback')
          .then(({ FeedbackModalComponent }) => {
            this.feedbackModal$.set(FeedbackModalComponent);
          })
          .catch(error => {
            console.error('Не удалось загрузить модальное окно обратной связи', error);
            this.feedbackService.close();
          })
          .finally(() => {
            this.feedbackModalLoading = false;
          });
      }
    });

    effect(() => {
      if (!this.paymentRequiredService.visible$() || this.paymentRequiredModal$() || this.paymentRequiredModalLoading) {
        return;
      }

      this.paymentRequiredModalLoading = true;
      import('@/shared/components/payment-required-modal')
        .then(({ PaymentRequiredModalComponent }) => {
          this.paymentRequiredModal$.set(PaymentRequiredModalComponent);
        })
        .catch(error => {
          console.error('Не удалось загрузить модальное окно оплаты', error);
          this.paymentRequiredService.cancel();
        })
        .finally(() => {
          this.paymentRequiredModalLoading = false;
        });
    });

    // Сценарий 2 (демо-лимит) и сценарий 4 (истёкшая дата): неактивная подписка —
    // ведём на страницу выбора тарифа. Платный тариф с нулевыми запросами остаётся
    // активным — для него модалка допзапросов, поэтому он сюда не попадает.
    effect(() => {
      const tariff = this.authService.tariff$();
      if (isSubscriptionActive(tariff)) {
        return;
      }

      const subscriptionExpiredPath = `${AppRoutingPathsEnum.Desktop}/${MainRoutingPathsEnum.SubscriptionExpired}`;
      if (this.router.url.startsWith(`/${subscriptionExpiredPath}`)) {
        return;
      }

      void this.router.navigate([AppRoutingPathsEnum.Desktop, MainRoutingPathsEnum.SubscriptionExpired]);
    });
  }
}
